import React, {useCallback, useEffect, useState, useRef} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {noop} from '@priority-software/priority-style-react';
import {formsStore} from '../../../stores/Form/FormsStore';
import {ScreenHeader} from './ScreenHeader/ScreenHeader';
import {SubForms} from './SubForms/SubForms';
import {apiLocker} from '../../../stores/Form/API_Locker';
import {ScreenLocker} from './ScreenLocker';
import {SelectListModalRefType, SelectListModal} from './SelectListModal';
import {listTypes} from './SelectListModal/enum';
import {Form} from './Form';
import {FocusViewDialog} from './FocusViewDialog';
import {SuspendedJobTypes} from '../consts/suspendedJobTypes';
import {createPortal} from 'react-dom';
import {scrollToViewStart} from '../utils/scrollToViewStart';
import {moveUp} from '../actions/screen';
import styles from '../styles/screen.module.scss';
import {createDataTestId} from '../../../utils/dataTestId';
import {getServerTexts} from './AdvancedSearch/AdvancedSearchDrawer/texts';
import {mailbox2510} from '../../Announcement/data/mailbox2510';
import {getServerTexts as getNEWFORMSTexts} from './SidePanelForms/texts';
import {Announcement} from '../../Announcement/components/Announcement';
import {isAnnouncementNew, setAnnouncementConfirmation} from '../../Announcement/service';
import {isAutomation} from '../../FeaturePopover/actions/feature';
import {FormViewEnum} from '../../../stores/Form/Form';
import {hideEmptyRecord, openNewEmptyRecord} from './FocusViewDialog/actions';

export const Screen = observer(() => {
    const modalRef = useRef<SelectListModalRefType>(null);
    const screenBodyRef = useRef<HTMLDivElement>(null);
    const [textsLoaded, setTextsLoaded] = useState(false);
    const {currentForm, currentScreen} = formsStore;
    const activeLevel = currentScreen?.activeLevel;
    const session = currentScreen?.session;

    const [announcements, setAnnouncements] = useState(null);
    const [showAnnouncement, setShowAnnouncement] = useState(false);
    const [showMailboxTogglePopup, setShowMailboxTogglePopup] = useState(false);

    useEffect(() => {
        getNEWFORMSTexts().then(() => {
            setAnnouncements(mailbox2510());
        });
    }, []);

    useEffect(() => {
        isAnnouncementNew('mailboxV2').then(isNew => {
            setShowAnnouncement(isNew);
        });
    }, [currentForm && currentForm.session]);

    const currScreenLockerData = apiLocker.getDataBySession(currentForm?.session);

    const onBreadCrumbClick = async (level: number, session: string) => {
        await moveUp(level, session);
    };

    const runSubForm = useCallback(
        async (subFormName: string, parentLevel: number, isHTMLForm?: boolean) => {
            const parentForm = formsStore.getFormByLevel(formsStore.currentSession, parentLevel);

            formsStore.setEventForRecordingMacro({
                type: SuspendedJobTypes.SubForm,
                formName: subFormName,
                parentForm: parentForm
            });
            await formsStore.currentScreen.runSubForm(subFormName, parentForm, isHTMLForm);
        },
        []
    );

    const openSelectListModal = useCallback((listType: listTypes) => {
        modalRef.current.openSelectListModal(listType);
    }, []);

    const disabledSubFormList = !!currentForm?.searchModeLevel;

    useEffect(() => {
        (async () => {
            setTextsLoaded(false);
            await getServerTexts();
            setTextsLoaded(true);
        })();
    }, []);

    useEffect(
        function scrollFormIntoViewport() {
            // Scroll active form into the viewport start
            const scrollElement = screenBodyRef.current;

            if (session && scrollElement) {
                const activeForm = formsStore.getFormByLevel(session, activeLevel);
                if (activeForm) {
                    scrollToViewStart(`#${activeForm.id}`, scrollElement);
                }
            }
        },
        [activeLevel, session]
    );

    const isApiLock = currentForm?.isApiLock || noop;

    const onAnnouncementClose = async (announcementVersion: string, showNextTime: boolean) => {
        setAnnouncementConfirmation(announcementVersion, showNextTime);
        setShowMailboxTogglePopup(true);
    };

    const renderForm = () => {
        if (!currentForm) return null;
        const isParentEmpty =
            currentForm.view === FormViewEnum.table &&
            currentForm.records.length === 1 &&
            currentForm.searchModeLevel === 0 &&
            currentForm.records[0].isEmpty();

        return (
            <div className={styles.screenBody} ref={screenBodyRef} id="screenBody">
                <div
                    className={cx(styles.masterForm, {
                        [styles.gridIndent]: currentForm.view === FormViewEnum.table,
                        [styles.emptyMasterForm]: isParentEmpty
                    })}
                    id={currentForm.id}
                    {...createDataTestId(`main-form-wrapper`, false)}
                >
                    <Form
                        hideNewEmptyRecord={hideEmptyRecord}
                        currentForm={currentForm}
                        isApiLock={isApiLock}
                        openSelectListModal={openSelectListModal}
                        componentClasses={cx(styles.sectionIndentX, {
                            [styles.fullHeight]: !isParentEmpty
                        })}
                        openNewEmptyRecord={openNewEmptyRecord}
                        isFormLocked={currentScreen?.isRecordDialog}
                        isEmptyForm={isParentEmpty}
                    />
                </div>

                {isParentEmpty ? null : (
                    <SubForms
                        disabledSubformList={disabledSubFormList}
                        subForm={currentForm.activeTabSubForm}
                        subFormsList={currentForm.subFormsList}
                        isApiLock={isApiLock}
                        openSelectListModal={openSelectListModal}
                        runSubForm={runSubForm}
                        parentLevel={currentForm.level}
                        isFormLocked={currentScreen?.isRecordDialog}
                    />
                )}
            </div>
        );
    };

    if (currentForm && textsLoaded) {
        if (currentScreen.standAlone) {
            return (
                <ScreenLocker
                    locked={currScreenLockerData?.locked}
                    showLoader={false}
                    toggleLoader={currScreenLockerData?.toggleLoader}
                />
            );
        }

        return (
            <div key={currentForm.session} className={styles.screenInner}>
                <ScreenHeader
                    componentClasses={styles.sectionIndentX}
                    session={currentForm.session}
                    breadcrumbs={currentScreen.breadcrumbs}
                    onBreadCrumbClick={onBreadCrumbClick}
                    showMailboxTogglePopup={showMailboxTogglePopup}
                    setShowMailboxTogglePopup={setShowMailboxTogglePopup}
                />

                {renderForm()}

                <ScreenLocker
                    locked={currScreenLockerData?.locked}
                    showLoader={currScreenLockerData?.showLoader}
                    toggleLoader={currScreenLockerData?.toggleLoader}
                />

                <SelectListModal ref={modalRef} isApiLock={isApiLock} />

                {currentScreen.isRecordDialog &&
                    createPortal(
                        <div className={cx('reactComp', styles.screenInner)}>
                            <FocusViewDialog
                                isFormActive={activeLevel === currentForm?.level}
                                currentForm={currentForm}
                                closeRecordDialog={hideEmptyRecord}
                                runSubForm={runSubForm}
                                disabledSubformList={disabledSubFormList}
                                isApiLock={isApiLock}
                            />
                        </div>,
                        document.body
                    )}

                {!isAutomation() &&
                    currentForm.ename === 'MAILBOXSEND' &&
                    announcements &&
                    showAnnouncement && (
                        <Announcement
                            announcements={announcements}
                            onMessageSeen={onAnnouncementClose}
                            announcementVersion={'mailboxV2'}
                        />
                    )}
            </div>
        );
    }

    return null;
});
